import React from "react";
import { SortableContainer, SortableElement } from "react-sortable-hoc";

import { PreferredLocale } from "../../../utils/locales";
import { AddInstructionPartWithDropZone } from "../../pages/CourseAuthor/CourseInstruction/Parts/AddInstructionPart";
import { CourseInstructionPart } from "../instruction/parts/CourseInstructionPart.component";
import { CourseInstructionPartOperations } from "../instruction/parts/CourseInstructionPart.types";
import { ICourseInstructionExerciseOperations } from "../instruction/types";
import { CourseUnitView, IAssessment, ICourseEditOperations, IQuestionPart } from "../types";
import { PartContainer, PartPlaceholder } from "./Assessment.styles";
import { useDragAndDrop } from "../../../context/UseDragAndDrop";
import { useTabIdContext } from "../../../context/TabIdContext";

interface ISortablePartProps {
  currentLocale: PreferredLocale;
  partOperations: CourseInstructionPartOperations;
  questionPart: IQuestionPart;
}

export interface ISortablePartListProps {
  assessment: IAssessment;
  courseId: string;
  currentLocale: PreferredLocale;
  editOperations: ICourseEditOperations;
  questionParts: IQuestionPart[];
  unitPath: CourseUnitView[];
}

const SortablePart = SortableElement((props: ISortablePartProps) => (
  <CourseInstructionPart
    contentPart={props.questionPart}
    currentLocale={props.currentLocale}
    isOpen={false}
    partOperations={props.partOperations}
  />
));

type ExerciseOpKey = keyof ICourseInstructionExerciseOperations;
type PartOpKey = keyof CourseInstructionPartOperations;

const operationKeys: ExerciseOpKey[] = [
  "addChoiceExerciseItem",
  "addClozeGap",
  "deleteClozeGap",
  "deleteInstructionPart",
  "deleteExerciseItem",
  "modifyChoiceExerciseItemCorrect",
  "modifyExerciseItemText",
  "modifyChoiceExerciseRandom",
  "modifyChoiceExerciseWrapperContent",
  "modifyClozeGapAnswers",
  "modifyClozeGapHint",
  "modifyClozeTemplate",
  "modifyExerciseItemHint",
];

export const SortablePartList = SortableContainer(
  ({ assessment, editOperations, currentLocale, questionParts, unitPath, courseId }: ISortablePartListProps) => {
    const questionOperations = operationKeys.reduce<ICourseInstructionExerciseOperations>((ops, key) => {
      return {
        ...ops,
        [key]: editOperations[key](unitPath, assessment),
      };
    }, {} as ICourseInstructionExerciseOperations);
    const { handleDragging } = useDragAndDrop();

    const { tabId: sourceTabId } = useTabIdContext();

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, questionPart: IQuestionPart) => {
      e.stopPropagation();
      e.dataTransfer.clearData();
      e.dataTransfer.setData(
        "text/plain",
        JSON.stringify({ data: questionPart, type: "question", courseId, sourceTabId })
      );
      handleDragging(true);
    };

    const handleDragEnd = () => handleDragging(false);
    return (
      <div>
        {questionParts
          .map<IQuestionPart>((q, i) => ({ ...q, sort: i }))
          .filter(question => !question.deleted)
          .map(question => (
            <React.Fragment key={question.id}>
              <AddInstructionPartWithDropZone
                dropPosition={question.id}
                dropZoneType="question"
                newInstructionType={null}
                editOperations={editOperations}
                path={unitPath}
                instructionSlide={assessment}
                addPart={editOperations.addPart(unitPath, assessment)}
                includeExplanation={false}
                includeGrids={false}
                displayOnStart={false}
                includeImages={false}
                includeDocuments={false}
                position={question.id}
              />
              <PartContainer
                key={question.id}
                draggable={true}
                // tslint:disable-next-line:jsx-no-lambda
                onDragStart={e => handleDragStart(e, question)}
                onDragEnd={handleDragEnd}
              >
                <SortablePart
                  currentLocale={currentLocale}
                  index={question.sort}
                  partOperations={getPartOperations(questionOperations, question)}
                  questionPart={question}
                />
                <PartPlaceholder />
              </PartContainer>
            </React.Fragment>
          ))}
      </div>
    );
  }
);

function getPartOperations(
  questionOperations: ICourseInstructionExerciseOperations,
  question: IQuestionPart
): CourseInstructionPartOperations {
  const noOp = () => null;
  const noOpKeys: PartOpKey[] = [
    "editHTMLContent",
    "editVoiceOverMedia",
    "editVoiceOverText",
    "editVoiceOverTime",
    "enableVoiceOverAudio",
  ];
  const opKeys: PartOpKey[] = Object.keys(questionOperations) as PartOpKey[];

  return opKeys.reduce<CourseInstructionPartOperations>((ops, key) => {
    return {
      ...ops,
      [key]: noOpKeys.includes(key) ? noOp : questionOperations[key](question),
    };
  }, {} as CourseInstructionPartOperations);
}
